import { Link, useParams } from "react-router-dom";
import { api } from "../api.ts";
import { ActorPicker, useActor } from "../components/ActorPicker.tsx";
import { TaskRow } from "../components/TaskRow.tsx";
import { AlertBanner, EmptyState } from "../components/ui.tsx";
import { useAsync } from "../hooks.ts";
import { FREQ_LABEL, FREQUENCIES } from "../../shared/schedule.ts";
import type { Me } from "../../shared/types.ts";

/** One room, its jobs grouped by how often they come round. */
export function Room({ me }: { me: Me }) {
  const { id } = useParams();
  const roomId = Number(id);
  const { data, error, loading, reload } = useAsync(() => api.house(), [roomId]);
  const people = useAsync(() => (me.role === "parent" ? api.people() : Promise.resolve([])));
  const { actorId, setActorId } = useActor(people.data ?? [], me.personId);

  if (error) return <AlertBanner>Couldn’t load the room. {error}</AlertBanner>;
  if (loading && !data) return <p className="muted">Loading…</p>;
  if (!data) return null;

  const floor = data.floors.find((f) => f.rooms.some((r) => r.id === roomId));
  const room = floor?.rooms.find((r) => r.id === roomId);

  if (!room || !floor) {
    return (
      <EmptyState
        icon="🚪"
        title="No such room"
        body="It may have been renamed or removed."
        action={
          <Link className="btn btn--ghost" to="/rooms">
            Back to the house
          </Link>
        }
      />
    );
  }

  return (
    <div>
      <header className="head">
        <span className="eyebrow">{floor.name}</span>
        <h1 className="head__title">
          <span aria-hidden="true">{room.emoji} </span>
          {room.name}
        </h1>
        <p className="head__note">
          {room.zone === "home" && data.away
            ? "✈️ Paused while you're away — the clock stopped, nothing is piling up."
            : room.zone === "travel" && !data.away
              ? "🧳 These wake up on the next trip."
              : room.actionable === 0
                ? "Nothing due here."
                : `${room.actionable} waiting`}
          {room.overdue > 0 ? ` · ${room.overdue} overdue` : ""}
        </p>
      </header>

      {me.role === "parent" && (people.data ?? []).length > 0 ? (
        <ActorPicker people={people.data ?? []} actorId={actorId} onChange={setActorId} />
      ) : null}

      {room.tasks.length === 0 ? (
        <EmptyState icon="🧽" title="No jobs in this room" body="A parent can add some in the admin page." />
      ) : (
        FREQUENCIES.map((freq) => {
          const tasks = room.tasks.filter((task) => task.freq === freq);
          if (tasks.length === 0) return null;
          return (
            <section key={freq}>
              <div className="section-head">
                <span className="eyebrow">{FREQ_LABEL[freq]}</span>
              </div>
              <ul className="tags">
                {tasks.map((task) => (
                  <TaskRow key={`${task.id}:${task.personId ?? 0}`} task={task} personId={actorId} onChanged={reload} />
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
